import React, { useMemo } from 'react'

import { Box, Paper, Typography } from '@mui/material'
import { BarChart } from '@tremor/react'

import {
    CHAVE_ROTULO_COMPLETO,
    CORES_HEX,
    avisoCores,
    avisoCorte,
    formatarValorGrafico,
} from './dadosGrafico'

const ALTURA_MINIMA = 320
// Um rótulo de até 2 linhas por barra deitada; menos que isso os nomes se atropelam.
const ALTURA_POR_BARRA = 32
// Rótulo com duas colunas ("PRODUTO · LOJA") ocupa até 3 linhas.
const ALTURA_POR_BARRA_DUPLA = 46
// Barras lado a lado (média): uma por série dentro de cada grupo.
const ALTURA_POR_SERIE = 14
const ALTURA_LEGENDA = 60
// Nome de loja encurtado em 36 letras cabe em uma ou duas linhas aqui.
const LARGURA_EIXO_TEXTO = 280

function listar(nomes) {
    return nomes.join(' e ')
}

// Tooltip próprio: o CustomTooltip do dashboard formata com um Intl dele e ignora
// o valueFormatter do gráfico — a barra mostrava "2.351,4" onde o eixo e a tabela
// mostram "R$ 2.351,40". Mesmo visual, valor pelo formatador do relatório.
export function TooltipRelatorio({ active, payload, label, tipo }) {
    if (!active || !payload?.length) {
        return null
    }

    // No eixo o nome vai encurtado; aqui cabe inteiro.
    const titulo = payload[0]?.payload?.[CHAVE_ROTULO_COMPLETO] || label

    return (
        <div className="rounded-md border border-gray-200 bg-white text-sm shadow-md dark:border-gray-800 dark:bg-gray-950">
            <div className="border-b border-inherit px-4 py-2">
                <p className="font-medium text-gray-900 dark:text-gray-50">{titulo}</p>
            </div>

            <div className="space-y-1 px-4 py-2">
                {payload.map((item, indice) => (
                    <div key={indice} className="flex items-center justify-between space-x-8">
                        <div className="flex items-center space-x-2">
                            <span
                                aria-hidden
                                className="h-2 w-2 shrink-0 rounded-sm"
                                style={{ backgroundColor: CORES_HEX[item.color] || CORES_HEX.blue }}
                            />
                            <p className="whitespace-nowrap text-gray-700 dark:text-gray-300">
                                {item.category || item.name}
                            </p>
                        </div>

                        <p className="text-right font-medium tabular-nums text-gray-900 dark:text-gray-50">
                            {formatarValorGrafico(item.value, tipo)}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function GraficoRelatorio({ grafico, containerRef }) {
    const tipoValor = grafico?.tipoValor
    // Identidade estável: função nova a cada render remontaria o tooltip no hover.
    const Tooltip = useMemo(
        () => function TooltipDoGrafico(props) {
            return <TooltipRelatorio {...props} tipo={tipoValor} />
        },
        [tipoValor],
    )

    if (!grafico) {
        return null
    }

    // "vertical" no tremor é barra deitada (nome herdado do recharts).
    const deitado = grafico.horizontal
    const series = grafico.categorias?.length || 1
    const comLegenda = series > 1
    const porBarra = grafico.rotuloDuplo ? ALTURA_POR_BARRA_DUPLA : ALTURA_POR_BARRA
    const porGrupo = grafico.empilhado || series === 1
        ? porBarra
        : Math.max(porBarra, ALTURA_POR_SERIE * series + 12)
    const legenda = comLegenda ? ALTURA_LEGENDA : 0
    const altura = deitado
        ? Math.max(ALTURA_MINIMA, grafico.dados.length * porGrupo + 40 + legenda)
        : ALTURA_MINIMA + legenda

    const aviso = avisoCorte(grafico)
    const cores = avisoCores(grafico)
    const forasDoGrafico = [
        ...(grafico.metricasForaDoGrafico || []),
        ...(grafico.colunasForaDoGrafico || []),
    ]

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} ref={containerRef}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {grafico.titulo}
            </Typography>

            <BarChart
                data={grafico.dados}
                index={grafico.indice}
                categories={grafico.categorias}
                colors={grafico.cores || ['blue']}
                layout={deitado ? 'vertical' : 'horizontal'}
                stack={!!grafico.empilhado}
                style={{ height: altura }}
                yAxisWidth={deitado ? LARGURA_EIXO_TEXTO : 90}
                showLegend={comLegenda}
                noDataText="Nenhum dado encontrado"
                valueFormatter={valor => formatarValorGrafico(valor, grafico.tipoValor)}
                customTooltip={Tooltip}
            />

            <Box sx={{ mt: 1 }}>
                {!!aviso && (
                    <Typography variant="body2" color="text.secondary">
                        {aviso}
                    </Typography>
                )}

                {!!cores && (
                    <Typography variant="body2" color="text.secondary">
                        {cores}
                    </Typography>
                )}

                {!!forasDoGrafico.length && (
                    <Typography variant="body2" color="text.secondary">
                        {`O gráfico mostra só ${grafico.conta || grafico.categorias[0]}. `}
                        {listar(forasDoGrafico)}{' '}
                        {forasDoGrafico.length > 1 ? 'estão' : 'está'} na tabela abaixo.
                    </Typography>
                )}
            </Box>
        </Paper>
    )
}
